import { NavLink, useSearchParams } from 'react-router-dom';
import { Inbox, Send, Trash2, Menu, RefreshCw, Loader2, Pen, Mail, FileText, Settings, Search, X, LayoutTemplate } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEmail } from '@/context/EmailContext';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useState, useEffect, useRef } from 'react';
import { toast } from "@/hooks/use-toast";
import EmailAccountsDialog from '@/components/email/settings/EmailAccountsDialog';

interface EmailToolbarProps {
  onComposeClick: () => void;
  onMenuClick?: () => void;
  usingMockData?: boolean;
  sidebarNavigation?: React.ReactNode;
}

const EmailToolbar = ({ 
  onComposeClick, 
  onMenuClick, 
  usingMockData, 
  sidebarNavigation 
}: EmailToolbarProps) => {
  const { 
    statistics, 
    loading,
    syncWithServer,
    refreshEmails
  } = useEmail();
  const isMobile = useIsMobile();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  // Search state lives in the URL (?q=) so folder views pick it up. Local
  // state is debounced before touching the URL to avoid a fetch per keystroke.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(urlQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the input in sync when the URL changes externally (back button,
  // folder switch clearing, etc.)
  useEffect(() => {
    setSearchInput(urlQuery);
  }, [urlQuery]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const applySearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set('q', value.trim());
    } else {
      next.delete('q');
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => applySearch(value), 400);
  };

  const clearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    applySearch('');
  };
  
  const handleCheckMail = async () => {
    if (isSyncing || loading) return; 
    setIsSyncing(true);
    toast.info("Syncing with mail server...");
    try {
      // syncWithServer now waits for completion
      const newEmailCount = await syncWithServer();

      // Update toast to show we're loading emails
      toast.info("Loading new emails...");
      await refreshEmails(); 
      
      if (newEmailCount > 0) {
        toast.success(`${newEmailCount} new email${newEmailCount === 1 ? '' : 's'} loaded`);
      } else {
        toast.success("No new emails");
      }
    } catch (error: any) {
       console.error("Check mail failed:", error);
       
       // Show specific error message for "no accounts" case
       if (error.message?.includes('No email accounts configured')) {
         toast.error("No email account configured. Please add an account in settings.");
       } else {
         toast.error("Failed to check mail server"); 
       }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing || loading) return;
    setIsRefreshing(true);
    toast.info("Refreshing email list...");
    try {
      await refreshEmails();
      toast.success("Email list refreshed");
    } catch (error) {
      console.error("Refresh failed:", error);
      toast.error("Failed to refresh email list");
    } finally {
      setIsRefreshing(false);
    }
  };
  
  return (
    <div className="border-b bg-background dark:bg-card shadow-sm">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          {isMobile && onMenuClick && (
            <Button variant="ghost" size="icon" onClick={onMenuClick}>
              <Menu className="h-4 w-4" />
              <span className="sr-only">Menu</span>
            </Button>
          )}
          
          {/* Gmail-style folder navigation */}
          <nav className="flex items-center gap-1">
            <NavLink 
              to="/app/email/inbox" 
              className={({ isActive }) => cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all",
                isActive 
                  ? "bg-primary/10 text-primary shadow-sm" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Inbox className="h-4 w-4" />
              <span>Inbox</span>
              {statistics.unread > 0 && (
                <Badge variant="secondary" className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 min-w-[20px] h-5 rounded-full">
                  {statistics.unread}
                </Badge>
              )}
            </NavLink>
            
            <NavLink 
              to="/app/email/sent" 
              className={({ isActive }) => cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all",
                isActive 
                  ? "bg-primary/10 text-primary shadow-sm" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Send className="h-4 w-4" />
              <span>Sent</span>
            </NavLink>
            
            <NavLink 
              to="/app/email/drafts" 
              className={({ isActive }) => cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all",
                isActive 
                  ? "bg-primary/10 text-primary shadow-sm" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <FileText className="h-4 w-4" />
              <span>Drafts</span>
            </NavLink>
            
            <NavLink 
              to="/app/email/trash" 
              className={({ isActive }) => cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all",
                isActive 
                  ? "bg-primary/10 text-primary shadow-sm" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Trash2 className="h-4 w-4" />
              <span>Trash</span>
            </NavLink>

            <NavLink 
              to="/app/email-templates" 
              className={({ isActive }) => cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all",
                isActive 
                  ? "bg-primary/10 text-primary shadow-sm" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <LayoutTemplate className="h-4 w-4" />
              <span>Templates</span>
            </NavLink>
          </nav>

          {!isMobile && (
            <div className="relative w-64 lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (debounceRef.current) clearTimeout(debounceRef.current);
                    applySearch(searchInput);
                  } else if (e.key === 'Escape') {
                    clearSearch();
                  }
                }}
                placeholder="Search mail..."
                className="pl-9 pr-8 h-9 bg-muted/40 border-border/50 focus-visible:bg-background"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh} 
            disabled={isRefreshing || loading}
            className="flex items-center gap-2"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </>
            )}
          </Button>

          <Button 
            variant="outline" 
            size="sm"
            onClick={handleCheckMail} 
            disabled={isSyncing || loading}
            className="flex items-center gap-2"
          >
            {isSyncing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Checking...</span>
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                <span>Check Mail</span>
              </>
            )}
          </Button>

          <Button 
            onClick={onComposeClick}
            className="flex items-center gap-2 px-6"
            size="sm"
          >
            <Pen className="h-4 w-4" />
            <span>Compose</span>
          </Button>

          <Button 
            onClick={() => setShowAccountSettings(true)}
            variant="ghost"
            size="sm"
            className="flex items-center gap-2"
            title="Email Account Settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <EmailAccountsDialog
        open={showAccountSettings}
        onOpenChange={setShowAccountSettings}
      />
    </div>
  );
};

export default EmailToolbar;
